import { Controller } from "stimulus"

// Owns the Bidwise Add funds experience: the single-view funding form (presets,
// amount input, card form, one submit), the single active Funding Attempt
// lifecycle, authoritative status polling, and the Balance/Funding history
// refresh after a confirmed credit. It talks only to the account-scoped Funding
// Attempt endpoints; every Stripe.js mechanic stays behind the composed
// "stripe" controller (events in both directions) and the modal behavior behind
// the composed "dialog" controller.
//
// The card form mounts on dialog open in Stripe's deferred-intent amount mode,
// so presets, amount, and card details are all visible together before any
// PaymentIntent exists. One submit runs the whole pipeline behind one in-flight
// guard: validate amount → validate card (elements.submit) → create/recover the
// attempt → confirm with the late client secret. Reload/3DS recovery instead
// mounts with the recovered client secret and locks the amount to the attempt.
//
// Browser state is never money authority here: a successful confirmation only
// starts polling, and the balance/history re-render always comes from the
// server-rendered Billing page.
//
// Usage (see billing/_add_funds.html.erb for the full markup):
//   <main data-controller="bidwise-client-funding"
//         data-bidwise-client-funding-create-url-value="/billing/funding-attempts"
//         data-bidwise-client-funding-billing-url-value="/billing"
//         data-bidwise-client-funding-min-dollars-value="1000"
//         data-bidwise-client-funding-max-dollars-value="100000"
//         data-action="dialog:opened->bidwise-client-funding#dialogOpened
//                      stripe:ready->bidwise-client-funding#stripeReady
//                      stripe:error->bidwise-client-funding#stripeError
//                      stripe:submit-ready->bidwise-client-funding#stripeSubmitReady
//                      stripe:confirm-error->bidwise-client-funding#confirmError
//                      stripe:confirm-submitted->bidwise-client-funding#confirmSubmitted">
export default class extends Controller {
  static targets = [
    "openButton", "pendingNotice",
    "dialogRoot", "form", "successStep",
    "presetButton", "amountInput", "amountError",
    "attemptSummary", "submitButton", "retryButton", "cancelButton", "statusRegion", "errorRegion",
    "stripeElement", "successSummary"
  ]
  static values = {
    createUrl: String, billingUrl: String,
    minDollars: Number, maxDollars: Number,
    attemptId: String, attemptStatus: String, attemptAmountCents: Number
  }

  connect() {
    this.submitting = false
    this.creating = false
    this.canceling = false
    // True after a confirmation was submitted without immediate error: the
    // payment is most likely completing, so the cancel escape hatch stays away
    // until polling settles the outcome.
    this.confirmationInFlight = false
    this.clientSecret = null
    // How the Payment Element is currently mounted: null (not yet), "amount"
    // (deferred-intent, no PaymentIntent), or "secret" (bound to a recovered
    // client secret). Decides whether submit needs the full create pipeline.
    this.elementMode = null
    this.pollTimer = null

    // A server-rendered active attempt means a payment is already in flight
    // (reload or 3DS return): resume authoritative polling immediately and keep
    // creation of another attempt unavailable.
    if (this.hasAttemptIdValue && this.attemptIdValue) {
      this.attempt = { id: this.attemptIdValue, status: this.attemptStatusValue, amountCents: this.attemptAmountCentsValue }
      this.showPendingNotice(this.activeAttemptNoticeCopy())
      this.startPolling()
    } else {
      this.attempt = null
    }
  }

  disconnect() {
    this.stopPolling()
  }

  // dialog:opened — always the same single view. An active attempt is resumed
  // with its amount locked and its client secret recovered; a fresh dialog
  // mounts the card form in amount mode so everything renders together.
  dialogOpened() {
    this.showView("form")

    if (this.attempt) {
      this.lockAmount()
      this.recoverClientSecret()
    } else {
      this.clearMessages()
      this.unlockAmount()
      this.mountForAmount()
    }
  }

  dialogClosed() {
    // Polling continues while the page is open so a slow webhook still lands in
    // the pending notice; only messages scoped to the dialog are cleared.
    this.clearMessages()
  }

  // Preset buttons fill the custom input so one validation path owns both.
  choosePreset(event) {
    this.amountInputTarget.value = event.currentTarget.dataset.amount
    this.syncPresetSelection()
    this.syncElementAmount()
  }

  customAmountChanged() {
    // The input is read-only while an attempt is active, but a stray event must
    // still never desync the element from the attempt's persisted amount.
    if (this.attempt) {
      return
    }

    this.syncPresetSelection()
    this.syncElementAmount()
  }

  // One place decides which preset reads as selected: the one matching the
  // current input value, however that value was produced.
  syncPresetSelection() {
    this.presetButtonTargets.forEach((button) => {
      const selected = button.dataset.amount === this.amountInputTarget.value
      button.classList.toggle("is-selected", selected)
      button.setAttribute("aria-pressed", selected ? "true" : "false")
    })
    this.setAmountError("")
  }

  // Keeps the amount-mode element's price in sync with valid input so Stripe's
  // own UI (e.g. installment hints) never shows a stale amount. Invalid input
  // changes nothing; submission re-validates anyway.
  syncElementAmount() {
    const raw = this.amountInputTarget.value.trim()

    if (this.validAmount(raw)) {
      this.stripeElementTarget.dispatchEvent(new CustomEvent("stripe:update-amount", { detail: { amount: Number(raw) * 100 } }))
    }
  }

  // The one Add funds submit: amount validation (client mirror of the server's
  // whole-dollar bounds — an invalid amount never reaches Stripe or the server)
  // → card validation → attempt creation → confirmation, all inside one
  // in-flight guard so duplicate submits cannot create a second PaymentIntent.
  submit(event) {
    event.preventDefault()

    if (this.submitting || this.creating || this.submitButtonTarget.disabled) {
      return
    }

    // Recovery view: the element is bound to the recovered client secret, so
    // confirmation happens directly against the mounted intent.
    if (this.elementMode === "secret") {
      if (this.clientSecret) {
        this.beginSubmit()
        this.stripeElementTarget.dispatchEvent(new CustomEvent("stripe:confirm"))
      }
      return
    }

    // Fresh flow: validate the amount before touching Stripe or the server.
    // With an attempt already adopted (e.g. retrying after a card decline), the
    // amount is locked to the attempt and needs no re-validation.
    if (!this.attempt) {
      const raw = this.amountInputTarget.value.trim()

      if (this.validAmount(raw)) {
        this.pendingAmountDollars = Number(raw)
      } else {
        this.setAmountError(`Enter a whole dollar amount between ${this.formatDollars(this.minDollarsValue * 100)} and ${this.formatDollars(this.maxDollarsValue * 100)}.`)
        this.amountInputTarget.focus()
        return
      }
    }

    this.beginSubmit()
    this.stripeElementTarget.dispatchEvent(new CustomEvent("stripe:submit"))
  }

  // stripe:submit-ready — the card form validated. An already-held client
  // secret (card-decline retry) confirms the same PaymentIntent without another
  // server call; otherwise the attempt is created/recovered first.
  stripeSubmitReady() {
    if (!this.submitting) {
      return
    }

    if (this.clientSecret) {
      this.stripeElementTarget.dispatchEvent(new CustomEvent("stripe:confirm", { detail: { clientSecret: this.clientSecret } }))
    } else {
      this.createOrRecoverAttempt(this.pendingAmountDollars, "confirm")
    }
  }

  // "Try again" after a retryable failure. With an active attempt the server
  // recovers the same attempt and client secret; without one (e.g. the Stripe
  // script failed to load) the amount-mode card form simply remounts.
  retry() {
    this.retryButtonTarget.hidden = true

    if (this.attempt) {
      this.setStatus("Preparing your secure payment…")
      this.createOrRecoverAttempt(null, "mount")
    } else {
      this.mountForAmount()
    }
  }

  // "Cancel and start over" for a locked attempt. The server releases the
  // attempt only when Stripe proves the intent can no longer charge; a payment
  // that actually succeeded keeps the locked view and polling credits it. Its
  // own in-flight guard makes duplicate clicks no-ops.
  async cancelAttempt() {
    if (!this.attempt || this.submitting || this.creating || this.canceling) {
      return
    }

    this.canceling = true
    this.cancelButtonTarget.disabled = true
    this.setError("")
    this.setStatus("Canceling this payment…")

    try {
      const response = await fetch(`${this.createUrlValue}/${this.attempt.id}`, {
        method: "DELETE",
        headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfToken() }
      })
      const body = await this.safeJson(response)

      if (response.status === 200 && body && body.failed) {
        // Provider-proved release: the same reset path terminal failure uses
        // (unlock amount, clear the notice, remount in amount mode), with
        // honest canceled copy in the alert region.
        this.handleFailed("Payment canceled. You have not been charged and can start a new payment.")
      } else if (response.status === 202 || (body && body.retryable)) {
        // Ambiguous/retryable (202): nothing changed server-side; the attempt
        // stays active and cancel stays offered.
        this.setStatus("")
        this.setError((body && body.error) || this.providerUnavailableCopy())
      } else if (response.ok && body) {
        // The payment actually completed (or is completing): keep the locked
        // view in its processing state and let authoritative polling resolve it.
        this.enterProcessing()
      } else {
        // Ambiguous/retryable: nothing changed server-side; cancel stays offered.
        this.setStatus("")
        this.setError((body && body.error) || "Payment processing is temporarily unavailable. Please try again shortly.")
      }
    } catch (error) {
      // Network failure proves nothing; the attempt is untouched and the
      // client may retry the cancel.
      this.setStatus("")
      this.setError(this.providerUnavailableCopy())
    } finally {
      this.canceling = false
      this.cancelButtonTarget.disabled = false
      this.syncCancelButton()
    }
  }

  // Creates or recovers the account's single active Funding Attempt. In-flight
  // and duplicate requests are refused so a second PaymentIntent can never be
  // requested from the browser. `onSecret` says what to do with the recovered
  // client secret: "confirm" continues the submit pipeline against the mounted
  // amount-mode element; "mount" rebinds the element to the secret (recovery).
  async createOrRecoverAttempt(amountDollars, onSecret) {
    if (this.creating) {
      return
    }

    this.creating = true
    this.syncCancelButton()

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify(amountDollars == null ? {} : { amount_dollars: amountDollars })
      })
      const body = await this.safeJson(response)

      if (response.ok && body && body.client_secret) {
        this.adoptAttempt(body)
        this.clientSecret = body.client_secret

        if (onSecret === "confirm") {
          this.stripeElementTarget.dispatchEvent(new CustomEvent("stripe:confirm", { detail: { clientSecret: this.clientSecret } }))
        } else {
          this.elementMode = "secret"
          this.stripeElementTarget.dispatchEvent(new CustomEvent("stripe:mount", { detail: { clientSecret: this.clientSecret } }))
        }
      } else if (response.status === 202 && body) {
        // The attempt stays active while the provider outcome is ambiguous;
        // surface the safe retryable copy without releasing the attempt.
        this.adoptAttempt(body)
        this.endSubmit()
        this.showRetryable(body.error)
      } else if (body && body.attempt_id && body.failed) {
        this.endSubmit()
        this.handleFailed(body.error)
      } else if (body && body.error) {
        this.endSubmit()
        this.submitButtonTarget.disabled = false
        this.setStatus("")
        this.setAmountError(body.error)
      } else {
        this.endSubmit()
        this.showRetryable(null)
      }
    } catch (error) {
      // Network failure proves nothing; keep whatever attempt state we have and
      // let the client retry the same recovery path.
      this.endSubmit()
      this.showRetryable(null)
    } finally {
      this.creating = false
      this.syncCancelButton()
    }
  }

  // Reopening an active attempt always asks the create/recover service for the
  // same PaymentIntent's transient client secret so the server revalidates the
  // intent; nothing is ever kept in browser storage, and remounting with an
  // unchanged secret is a no-op at the provider seam.
  recoverClientSecret() {
    this.submitButtonTarget.disabled = true
    this.setStatus("Loading secure card form…")
    this.createOrRecoverAttempt(null, "mount")
  }

  // Mounts the card form in deferred-intent amount mode so it renders before
  // any PaymentIntent exists; the initial amount is the first preset.
  mountForAmount() {
    this.elementMode = "amount"
    this.submitButtonTarget.disabled = true
    this.setStatus("Loading secure card form…")
    this.stripeElementTarget.dispatchEvent(new CustomEvent("stripe:mount-amount", { detail: { amount: this.initialElementAmountCents() } }))
  }

  stripeReady() {
    if (!this.submitting) {
      this.submitButtonTarget.disabled = false
    }
    this.retryButtonTarget.hidden = true
    this.setStatus("")
  }

  stripeError(event) {
    this.endSubmit()
    this.showRetryable(event.detail && event.detail.message)
  }

  // Immediate definitive confirmation or card-validation error: the attempt and
  // PaymentIntent (when they exist) stay active, so corrected card details
  // retry that PaymentIntent instead of creating another one.
  confirmError(event) {
    this.endSubmit()
    this.submitButtonTarget.disabled = false
    this.setStatus("")
    this.setError(event.detail && event.detail.message)
  }

  // Confirmation submitted without immediate error: processing, never success.
  // Money appears only when polling reports the verified webhook credit. The
  // dialog stays locked (open) through processing per the UX contract; it
  // unlocks when polling reaches a credited or failed outcome.
  confirmSubmitted() {
    this.submitting = false
    this.enterProcessing()
  }

  enterProcessing() {
    // The payment is most likely completing; hide the cancel escape hatch so
    // the client is never invited to cancel money that is about to land.
    this.confirmationInFlight = true
    this.submitButtonTarget.disabled = true
    this.retryButtonTarget.hidden = true
    this.syncCancelButton()
    this.setError("")
    this.setStatus(this.processingCopy())
    this.showPendingNotice(this.activeAttemptNoticeCopy())
    this.startPolling()
  }

  // --- Authoritative status polling (bounded backoff, page-lifetime) ---

  startPolling() {
    if (this.pollTimer || !this.attempt) {
      return
    }

    this.pollDelay = 2000
    this.schedulePoll()
  }

  schedulePoll() {
    this.pollTimer = setTimeout(() => this.poll(), this.pollDelay)
    // Bounded backoff: quick at first, then settles at a slow steady interval
    // for as long as the webhook takes.
    this.pollDelay = Math.min(this.pollDelay * 2, 15000)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  }

  async poll() {
    this.pollTimer = null
    if (!this.attempt) {
      return
    }

    try {
      const response = await fetch(`${this.createUrlValue}/${this.attempt.id}`, { headers: { "Accept": "application/json" } })

      if (response.ok) {
        const body = await this.safeJson(response)

        // The server's explicit terminal booleans are the only decision
        // inputs; the browser never interprets server-internal status strings.
        if (body && body.credited) {
          this.handleCredited(body)
          return
        }
        if (body && body.failed) {
          this.handleFailed(null)
          return
        }
      }
    } catch (error) {
      // Transient polling failures change nothing; the attempt state on the
      // server remains authoritative and the next poll re-reads it.
    }

    this.schedulePoll()
  }

  // Verified webhook credit confirmed: announce the persisted amount and
  // re-render Balance/Funding history from the authoritative server response.
  handleCredited(body) {
    this.stopPolling()
    this.lockDialog(false)
    const announcement = `${this.formatDollars(body.amount_cents)} added to your balance`
    this.attempt = null
    this.clientSecret = null
    this.elementMode = null
    this.confirmationInFlight = false
    this.syncCancelButton()

    this.successSummaryTarget.textContent = announcement
    this.setStatus(announcement)
    this.showView("success")
    this.showPendingNotice(announcement)
    this.refreshBillingSections()
  }

  // Terminal failure: the attempt can no longer charge, so the single view
  // unlocks for a new deliberate attempt. The failed intent's secret can no
  // longer be used, so an open dialog remounts the card form in amount mode.
  handleFailed(message) {
    this.stopPolling()
    this.attempt = null
    this.clientSecret = null
    this.pendingAmountDollars = null
    this.submitting = false
    this.confirmationInFlight = false
    this.lockDialog(false)
    this.hidePendingNotice()

    this.showView("form")
    this.unlockAmount()
    this.setAmountError(message || "This payment could not be completed and you have not been charged. You can start a new payment.")
    this.setStatus("")
    this.setError("")

    if (this.dialogOpen()) {
      this.mountForAmount()
    } else {
      this.elementMode = null
    }
  }

  // Fetches the server-rendered Billing page and swaps in the authoritative
  // Balance, Funding history, and top bar balance pill. No client-side math.
  async refreshBillingSections() {
    try {
      const response = await fetch(this.billingUrlValue, { headers: { "Accept": "text/html" } })
      if (!response.ok) {
        return
      }

      const markup = await response.text()
      const refreshed = new DOMParser().parseFromString(markup, "text/html")

      const replacements = [ ".bwp-billing-balance__amount", ".bwp-funding-history", ".bwp-balance-pill" ]
      replacements.forEach((selector) => {
        const current = document.querySelector(selector)
        const next = refreshed.querySelector(selector)
        if (current && next) {
          current.replaceWith(next)
        }
      })
    } catch (error) {
      // The credit is already announced; a failed refresh only means the next
      // full page load shows the new balance instead.
    }
  }

  // --- Presentation helpers ---

  beginSubmit() {
    this.submitting = true
    this.lockDialog(true)
    this.submitButtonTarget.disabled = true
    this.syncCancelButton()
    this.setError("")
    this.setStatus("Confirming your payment…")
  }

  // Releases the in-flight guard and the dialog lock; each terminal handler
  // decides the submit button and message state itself.
  endSubmit() {
    this.submitting = false
    this.lockDialog(false)
    this.syncCancelButton()
  }

  showRetryable(message) {
    this.submitButtonTarget.disabled = true
    this.retryButtonTarget.hidden = false
    this.setStatus("")
    this.setError(message || this.providerUnavailableCopy())
  }

  // Canonical fallback copy for every ambiguous provider/network outcome; the
  // server's own error message wins when one is present.
  providerUnavailableCopy() {
    return "Payment processing is temporarily unavailable. Please try again shortly."
  }

  adoptAttempt(body) {
    this.attempt = { id: body.attempt_id, status: body.status, amountCents: body.amount_cents }
    this.lockAmount()
    this.showPendingNotice(this.activeAttemptNoticeCopy())
  }

  showView(name) {
    this.formTarget.hidden = name !== "form"
    this.successStepTarget.hidden = name !== "success"
  }

  // While an attempt is active the amount belongs to its PaymentIntent: the
  // input renders the persisted amount read-only and the presets lock, so the
  // single view can never desync from what the attempt will charge.
  lockAmount() {
    this.amountInputTarget.value = String(Math.round(this.attempt.amountCents / 100))
    this.amountInputTarget.readOnly = true
    this.amountInputTarget.setAttribute("aria-readonly", "true")
    this.presetButtonTargets.forEach((button) => { button.disabled = true })
    this.syncPresetSelection()
    this.attemptSummaryTarget.textContent = `Adding ${this.formatDollars(this.attempt.amountCents)} to your balance`
    this.attemptSummaryTarget.hidden = false
    this.syncCancelButton()
  }

  unlockAmount() {
    this.amountInputTarget.readOnly = false
    this.amountInputTarget.removeAttribute("aria-readonly")
    this.presetButtonTargets.forEach((button) => { button.disabled = false })
    this.attemptSummaryTarget.textContent = ""
    this.attemptSummaryTarget.hidden = true
    this.syncCancelButton()
  }

  // The cancel escape hatch is offered only for a locked attempt that nothing
  // is currently deciding: never while a submit/confirmation/creation is in
  // flight, and never once a confirmation was submitted without error.
  syncCancelButton() {
    const available = !!this.attempt && !this.submitting && !this.creating && !this.confirmationInFlight
    this.cancelButtonTarget.hidden = !available
  }

  // The pending notice is the page-level (outside the dialog) view of the
  // in-flight attempt; it also blocks nothing because the server owns the
  // one-active-attempt rule.
  showPendingNotice(text) {
    this.pendingNoticeTarget.textContent = text
    this.pendingNoticeTarget.hidden = false
    this.openButtonTarget.textContent = this.attempt ? "View payment status" : "Add funds"
  }

  hidePendingNotice() {
    this.pendingNoticeTarget.hidden = true
    this.openButtonTarget.textContent = "Add funds"
  }

  dialogOpen() {
    return this.dialogRootTarget.querySelector("dialog[open]") !== null
  }

  lockDialog(locked) {
    this.dialogRootTarget.setAttribute("data-dialog-locked-value", locked ? "true" : "false")
  }

  setStatus(text) {
    this.statusRegionTarget.textContent = text
  }

  setError(text) {
    this.errorRegionTarget.textContent = text || ""
  }

  setAmountError(text) {
    this.amountErrorTarget.textContent = text || ""
  }

  clearMessages() {
    this.setError("")
    this.setAmountError("")
    if (!this.attempt) {
      this.setStatus("")
    }
  }

  validAmount(raw) {
    return /^\d+$/.test(raw) && Number(raw) >= this.minDollarsValue && Number(raw) <= this.maxDollarsValue
  }

  // The element must carry some amount before the client picks one; the first
  // preset (or the configured minimum) stands in until the input syncs it.
  initialElementAmountCents() {
    const firstPreset = this.presetButtonTargets[0]
    const dollars = firstPreset ? Number(firstPreset.dataset.amount) : this.minDollarsValue
    return dollars * 100
  }

  // Identical pending copy for every amount; there is no delayed-confirmation
  // variant.
  processingCopy() {
    return "Your payment is processing. Your balance updates only after the payment is confirmed — do not resubmit or start another payment. If this does not complete, contact support."
  }

  // The one page-level label for any active attempt, whatever its client-side
  // history (fresh, declined, mid-processing, recovered after reload). A decline
  // is not persisted server-side, so per-state page copy could never stay
  // consistent across reloads; the dialog is the single place that shows the
  // precise state and available actions.
  activeAttemptNoticeCopy() {
    return `A ${this.formatDollars(this.attempt.amountCents)} payment is in progress — view payment status to finish or cancel it.`
  }

  // Whole-dollar display for persisted cents, e.g. 150000 -> "$1,500".
  formatDollars(cents) {
    const dollars = cents / 100
    return dollars.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
      maximumFractionDigits: 2
    })
  }

  async safeJson(response) {
    try {
      return await response.json()
    } catch (error) {
      return null
    }
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
